import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  FormLayout,
  TextField,
  Checkbox,
  Select,
  RangeSlider,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Badge,
  Banner,
  Divider,
  Button,
  Thumbnail,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { BundleSettings } from '../types';

interface BundleSettingsFormProps {
  settings: BundleSettings;
  onChange: (settings: BundleSettings) => void;
}

export function BundleSettingsForm({ settings, onChange }: BundleSettingsFormProps) {
  const { t } = useTranslation();
  const [newOverrideMode, setNewOverrideMode] = useState<string>('auto');
  // Map productId → { title, image } for display (populated by resource picker)
  const [productNames, setProductNames] = useState<Record<string, { title: string; image?: string }>>({}); 
  const handleChange = useCallback(
    <K extends keyof BundleSettings>(field: K, value: BundleSettings[K]) => {
      onChange({ ...settings, [field]: value });
    },
    [settings, onChange]
  );

  const displayStyleOptions = [
    { label: t('bundles.displayStyleInline'), value: 'inline' },
    { label: t('bundles.displayStylePopup'), value: 'popup' },
    { label: t('bundles.displayStyleDrawer'), value: 'drawer' },
  ];

  return (
    <BlockStack gap="400">
      {/* Auto-generation settings */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {t('bundles.autoGeneration')}
            </Text>
            <Badge tone={settings.autoGenerateEnabled ? 'success' : 'attention'}>
              {settings.autoGenerateEnabled ? t('dashboard.widgets.enabled') : t('dashboard.widgets.disabled')}
            </Badge>
          </InlineStack>

          <Checkbox
            label={t('bundles.enableAutoGeneration')}
            helpText={t('bundles.autoGenerationHelp')}
            checked={settings.autoGenerateEnabled}
            onChange={(checked) => handleChange('autoGenerateEnabled', checked)}
          />

          {settings.autoGenerateEnabled && (
            <Box paddingBlockStart="400">
              <BlockStack gap="400">
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label={t('bundles.minFbtScore')}
                      type="number"
                      value={settings.minFbtScore.toString()}
                      onChange={(value) => handleChange('minFbtScore', parseFloat(value) || 0)}
                      helpText={t('bundles.minFbtScoreHelp')}
                      autoComplete="off"
                      min={0}
                      max={1}
                      step={0.1}
                    />
                    <TextField
                      label={t('bundles.maxBundlesPerProduct')}
                      type="number"
                      value={settings.maxBundlesPerProduct.toString()}
                      onChange={(value) => handleChange('maxBundlesPerProduct', parseInt(value) || 3)}
                      helpText={t('bundles.maxBundlesPerProductHelp')}
                      autoComplete="off"
                      min={1}
                      max={10}
                    />
                  </FormLayout.Group>

                  <FormLayout.Group>
                    <TextField
                      label={t('bundles.minProductsPerBundle')}
                      type="number"
                      value={settings.minProductsPerBundle.toString()}
                      onChange={(value) => handleChange('minProductsPerBundle', parseInt(value) || 2)}
                      helpText={t('bundles.minProductsPerBundleHelp')}
                      autoComplete="off"
                      min={2}
                      max={10}
                    />
                    <TextField
                      label={t('bundles.maxProductsPerBundle')}
                      type="number"
                      value={settings.maxProductsPerBundle.toString()}
                      onChange={(value) => handleChange('maxProductsPerBundle', parseInt(value) || 4)}
                      helpText={t('bundles.maxProductsPerBundleHelp')}
                      autoComplete="off"
                      min={2}
                      max={10}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Box>
          )}
        </BlockStack>
      </Card>

      {/* Discount settings */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t('bundles.discountSettings')}
          </Text>

          <Box>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t('bundles.defaultDiscountLabel', { percent: settings.defaultDiscountPercentage })}
            </Text>
            <Box paddingBlockStart="200">
              <RangeSlider
                label={t('bundles.defaultDiscountPercentage')}
                value={settings.defaultDiscountPercentage}
                onChange={(value) => handleChange('defaultDiscountPercentage', value as number)}
                min={5}
                max={50}
                step={5}
                suffix={<Text as="span">{settings.defaultDiscountPercentage}%</Text>}
              />
            </Box>
          </Box>

          <Banner tone="info">
            <p>
              {t('bundles.discountBannerInfo')}
            </p>
          </Banner>

          <Divider />

          <FormLayout>
            <FormLayout.Group>
              <TextField
                label={t('bundles.minBundlePrice')}
                type="number"
                value={settings.minBundlePrice?.toString() || ''}
                onChange={(value) => handleChange('minBundlePrice', value ? parseFloat(value) : undefined)}
                helpText={t('bundles.minBundlePriceHelp')}
                autoComplete="off"
                prefix="$"
              />
              <TextField
                label={t('bundles.maxBundlePrice')}
                type="number"
                value={settings.maxBundlePrice?.toString() || ''}
                onChange={(value) => handleChange('maxBundlePrice', value ? parseFloat(value) : undefined)}
                helpText={t('bundles.maxBundlePriceHelp')}
                autoComplete="off"
                prefix="$"
              />
            </FormLayout.Group>
          </FormLayout>
        </BlockStack>
      </Card>

      {/* Auto-Discount Mode */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {t('bundles.autoDiscountTitle')}
            </Text>
            <Badge tone={settings.autoDiscountMode === 'auto' ? 'success' : settings.autoDiscountMode === 'off' ? 'critical' : 'attention'}>
              {settings.autoDiscountMode === 'auto' ? t('bundles.modeAutomatic') : settings.autoDiscountMode === 'off' ? t('bundles.modeDisabled') : t('bundles.modeManual')}
            </Badge>
          </InlineStack>

          <Select
            label={t('bundles.discountCreationModeLabel')}
            options={[
              { label: t('bundles.discountModeManualDesc'), value: 'manual' },
              { label: t('bundles.discountModeAutoDesc'), value: 'auto' },
              { label: t('bundles.discountModeOffDesc'), value: 'off' },
            ]}
            value={settings.autoDiscountMode ?? 'manual'}
            onChange={(value) => handleChange('autoDiscountMode', value as 'auto' | 'manual' | 'off')}
            helpText={t('bundles.discountCreationModeHelp')}
          />

          {settings.autoDiscountMode === 'auto' && (
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t('bundles.discountTypeLabel')}
                  options={[
                    { label: t('bundles.percentageOff'), value: 'percentage' },
                    { label: t('bundles.fixedAmountOff'), value: 'fixed' },
                  ]}
                  value={settings.autoDiscountType ?? 'percentage'}
                  onChange={(value) => handleChange('autoDiscountType', value)}
                />
                <TextField
                  label={t('bundles.discountValueLabel')}
                  type="number"
                  value={String(settings.autoDiscountValue ?? settings.defaultDiscountPercentage)}
                  onChange={(value) => handleChange('autoDiscountValue', parseFloat(value) || 0)}
                  suffix={settings.autoDiscountType === 'fixed' ? '$' : '%'}
                  helpText={t('bundles.discountValueHelp')}
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
          )}

          <Banner tone="info">
            <p>
              {t('bundles.perProductBannerInfo')}
            </p>
          </Banner>
        </BlockStack>
      </Card>

      {/* Per-Product Discount Overrides */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {t('bundles.perProductOverridesTitle')}
            </Text>
            <Badge>
              {t('bundles.overridesCount', { count: Object.keys(settings.perProductDiscountOverrides || {}).length })}
            </Badge>
          </InlineStack>

          <Text as="p" tone="subdued">
            {t('bundles.perProductOverridesDesc')}
          </Text>

          {Object.keys(settings.perProductDiscountOverrides || {}).length > 0 && (
            <BlockStack gap="200">
              {Object.entries(settings.perProductDiscountOverrides || {}).map(
                ([productId, mode]) => (
                  <InlineStack key={productId} gap="300" blockAlign="center" align="space-between">
                    <InlineStack gap="200" blockAlign="center">
                      {productNames[productId]?.image && (
                        <Thumbnail source={productNames[productId].image!} alt="" size="small" />
                      )}
                      <BlockStack gap="050">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {productNames[productId]?.title || `Product ${productId}`}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          ID: {productId}
                        </Text>
                      </BlockStack>
                      <Badge tone={mode === 'auto' ? 'success' : mode === 'off' ? 'critical' : 'attention'}>
                        {mode === 'auto' ? t('bundles.modeAutomatic') : mode === 'off' ? t('bundles.modeDisabled') : t('bundles.modeManual')}
                      </Badge>
                    </InlineStack>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => {
                        const updated = { ...(settings.perProductDiscountOverrides || {}) };
                        delete updated[productId];
                        onChange({ ...settings, perProductDiscountOverrides: updated });
                        // Clean up label cache
                        setProductNames(prev => {
                          const next = { ...prev };
                          delete next[productId];
                          return next;
                        });
                      }}
                    >
                      {t('common.remove')}
                    </Button>
                  </InlineStack>
                )
              )}
            </BlockStack>
          )}

          <Divider />

          <Text variant="headingSm" as="h3">{t('bundles.addOverrideTitle')}</Text>
          <BlockStack gap="200">
            <InlineStack gap="300" align="start" blockAlign="end">
              <Box minWidth="150px" maxWidth="200px">
                <Select
                  label={t('bundles.discountModeLabel')}
                  options={[
                    { label: t('bundles.modeAutomatic'), value: 'auto' },
                    { label: t('bundles.modeManual'), value: 'manual' },
                    { label: t('bundles.modeOff'), value: 'off' },
                  ]}
                  value={newOverrideMode}
                  onChange={setNewOverrideMode}
                />
              </Box>
              <Box>
                <Button
                  icon={SearchIcon}
                  onClick={async () => {
                    try {
                      const selected = await window.shopify?.resourcePicker({
                        type: 'product',
                        multiple: true,
                        filter: { hidden: false, variants: false, draft: false, archived: false },
                      });
                      if (selected && selected.length > 0) {
                        const updated = { ...(settings.perProductDiscountOverrides || {}) };
                        const names = { ...productNames };
                        for (const product of selected) {
                          // Extract numeric ID from GID format: "gid://shopify/Product/12345" → "12345"
                          const numericId = product.id.replace(/^gid:\/\/shopify\/Product\//, '');
                          updated[numericId] = newOverrideMode;
                          names[numericId] = {
                            title: product.title,
                            image: product.images?.[0]?.originalSrc,
                          };
                        }
                        setProductNames(names);
                        onChange({ ...settings, perProductDiscountOverrides: updated });
                      }
                    } catch (err) {
                      console.error('Resource picker error:', err);
                    }
                  }}
                >
                  {t('bundles.browseProducts')}
                </Button>
              </Box>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              {t('bundles.browseProductsHelp')}
            </Text>
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Display settings */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t('bundles.displaySettings')}
          </Text>

          <FormLayout>
            <Select
              label={t('bundles.displayStyle')}
              options={displayStyleOptions}
              value={settings.displayStyle}
              onChange={(value) => handleChange('displayStyle', value as BundleSettings['displayStyle'])}
              helpText={t('bundles.displayStyleHelp')}
            />

            <TextField
              label={t('bundles.ctaButtonText')}
              value={settings.bundleCtaText}
              onChange={(value) => handleChange('bundleCtaText', value)}
              helpText={t('bundles.ctaButtonTextHelp')}
              autoComplete="off"
            />

            <Checkbox
              label={t('bundles.showSavingsBadge')}
              helpText={t('bundles.showSavingsBadgeHelp')}
              checked={settings.showSavingsBadge}
              onChange={(checked) => handleChange('showSavingsBadge', checked)}
            />
          </FormLayout>
        </BlockStack>
      </Card>

      {/* Info card */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            {t('bundles.howBundlesWork')}
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            {t('bundles.bundlesGeneratedUsing')}
          </Text>
          <BlockStack gap="200">
            <InlineStack gap="200" align="start">
              <Badge tone="success">40%</Badge>
              <Text as="span" variant="bodyMd">{t('bundles.fbtData')}</Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Badge tone="info">30%</Badge>
              <Text as="span" variant="bodyMd">{t('bundles.similarityScores')}</Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Badge>20%</Badge>
              <Text as="span" variant="bodyMd">{t('bundles.complementaryCategories')}</Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Badge tone="attention">10%</Badge>
              <Text as="span" variant="bodyMd">{t('bundles.marginOptimization')}</Text>
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
